import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FloatingToolbar } from './FloatingToolbar';
import { TokenCounter } from './TokenCounter';
import { VersionHistory } from './VersionHistory';
import { SEOContent } from './SEOContent';
import { FAQSection } from './FAQSection';
import { OpenRouterConfig, OpenRouterConfig as OpenRouterConfigType } from './OpenRouterConfig';
import { PromptPreview } from './PromptPreview';
import { InlineAutocompleteSuggestion } from './InlineAutocompleteSuggestion';
import { QuickPreviewModal } from './QuickPreviewModal';
import { SnippetsModal } from './SnippetsModal';
import { MagicWand } from './MagicWand';
import { MultimodalWorkflow } from './MultimodalWorkflow';
import { useAutoSave } from '../hooks/useAutoSave';
import { useTemplateSnippets } from '../hooks/useTemplateSnippets';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useIntelligentAutocomplete } from '../hooks/useIntelligentAutocomplete';
import { useSnippets } from '../hooks/useSnippets';
import { OpenRouterService, PromptEvaluation, PromptSuggestion } from '../services/openRouterService';
import { LanguageSelector } from './LanguageSelector';
import { CookieConsent } from './CookieConsent';
import { useLanguage } from '../hooks/useLanguage';
import { useEvaluationHistory } from '../hooks/useEvaluationHistory';
import { useTranslation } from 'react-i18next';
import { 
  trackPromptCreated, 
  trackPromptEvaluated, 
  trackPromptExported, 
  trackLanguageChanged, 
  trackConfigurationSaved,
  trackSuggestionApplied,
  trackFocusModeToggled,
  trackVersionHistoryAccessed
} from '../services/firebase';

interface PromptEditorProps {
  className?: string;
}

export const PromptEditor: React.FC<PromptEditorProps> = ({ className = '' }) => {
  const [content, setContent] = useState('');
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectionRange, setSelectionRange] = useState<{start: number, end: number} | null>(null);
  const [openRouterConfig, setOpenRouterConfig] = useState<OpenRouterConfigType | null>(null);
  const [evaluation, setEvaluation] = useState<PromptEvaluation | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationError, setEvaluationError] = useState<string | null>(null);
  const [openRouterService, setOpenRouterService] = useState<OpenRouterService | null>(null);
  const [evaluatedContent, setEvaluatedContent] = useState<string>('');
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [quickPreviewResult, setQuickPreviewResult] = useState<string>('');
  const [isQuickPreviewing, setIsQuickPreviewing] = useState(false);
  const [showQuickPreview, setShowQuickPreview] = useState(false);
  const [showSnippets, setShowSnippets] = useState(false);
  const [autoOpenSaveDialog, setAutoOpenSaveDialog] = useState(false);
  const [showMultimodal, setShowMultimodal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const { saveStatus } = useAutoSave(content);
  const { handleSlashCommand, suggestions } = useTemplateSnippets(content, setContent);
  const { language, isPortuguese } = useLanguage();
  const { hasRecentEvaluation, addEvaluation } = useEvaluationHistory();
  const { t } = useTranslation();
  const { addSnippet, reloadSnippets } = useSnippets(isPortuguese ? 'pt' : 'en');

  // Callback to handle content updates from autocomplete
  const handleContentUpdate = useCallback((newContent: string, newCursorPos: number) => {
    setContent(newContent);
    setCursorPosition(newCursorPos);
    
    // Update textarea cursor position after content update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  }, []);

  // Initialize autocomplete hook
  const {
    suggestion: autocompleteSuggestion,
    isLoading: isAutocompleteLoading,
    triggerSuggestion,
    acceptSuggestion,
    rejectSuggestion
  } = useIntelligentAutocomplete(content, cursorPosition, openRouterService, textareaRef, handleContentUpdate);

  // Token estimation function - defined early to avoid hoisting issues
  const estimateTokens = useCallback((text: string): number => {
    // Simple token estimation: ~4 characters per token for GPT models
    return Math.ceil(text.length / 4);
  }, []);
  
  useKeyboardShortcuts({
    onFocusToggle: () => {
      const newFocusMode = !isFocusMode;
      setIsFocusMode(newFocusMode);
      trackFocusModeToggled(newFocusMode);
    },
    onExport: () => exportContent(),
    onHistory: () => {
      setShowHistory(true);
      trackVersionHistoryAccessed();
    },
    onEvaluate: () => handleEvaluatePrompt(),
    onTriggerOrAcceptAutocomplete: () => {
      if (autocompleteSuggestion) {
        // If there's a suggestion visible, accept it
        acceptSuggestion();
      } else {
        // If no suggestion, trigger a new one
        triggerSuggestion();
      }
    },
    onRejectSuggestion: () => {
      if (autocompleteSuggestion) {
        rejectSuggestion();
        return true;
      }
      return false;
    },
    onToggleSnippets: () => {
      setShowSnippets(!showSnippets);
    },
    onSaveSnippet: () => {
      if (content.trim()) {
        setAutoOpenSaveDialog(true);
        setShowSnippets(true);
      }
    }
  });

  // Load OpenRouter config from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('openrouter-config');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setOpenRouterConfig(config);
        setOpenRouterService(new OpenRouterService(config));
      } catch (error) {
        console.error('Error loading OpenRouter config:', error);
      }
    }
  }, []);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const previousLength = content.length;
    const cursorPos = e.target.selectionStart;
    
    setContent(value);
    setCursorPosition(cursorPos);
    
    // Track prompt creation when user types substantial content
    if (value.length > 50 && previousLength <= 50) {
      const wordCount = value.split(/\s+/).filter(word => word.length > 0).length;
      const tokenCount = estimateTokens(value);
      trackPromptCreated(wordCount, tokenCount);
    }
    
    // Handle slash commands
    handleSlashCommand(value, cursorPos);
  }, [handleSlashCommand, content.length, estimateTokens]);

  const handleSelection = useCallback(() => {
    if (!textareaRef.current) return;
    
    const { selectionStart, selectionEnd } = textareaRef.current;
    
    // Update cursor position for autocomplete
    setCursorPosition(selectionStart);
    
    if (selectionStart !== selectionEnd) {
      setSelectionRange({ start: selectionStart, end: selectionEnd });
      // Hide autocomplete when text is selected
      rejectSuggestion();
    } else {
      setSelectionRange(null);
    }
  }, [rejectSuggestion]);

  const exportContent = useCallback(() => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Track export
    const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
    trackPromptExported('txt', wordCount);
  }, [content]);

  const handleConfigSave = useCallback((config: OpenRouterConfigType) => {
    setOpenRouterConfig(config);
    localStorage.setItem('openrouter-config', JSON.stringify(config));
    setOpenRouterService(new OpenRouterService(config));
    
    // Track configuration
    trackConfigurationSaved('openrouter', config.model);
  }, []);

  const handleEvaluatePrompt = useCallback(async (forceNew: boolean = false) => {
    if (!openRouterService || !content.trim()) {
      if (!openRouterService) {
        alert(t('alerts.configureApiKey'));
        setShowConfig(true);
      }
      return;
    }

    // Check for recent evaluation if not forcing new analysis
    if (!forceNew && openRouterConfig) {
      const recentEval = hasRecentEvaluation(content, language, openRouterConfig.model);
      if (recentEval) {
        setEvaluation(recentEval.evaluation);
        setShowPreview(true);
        return;
      }
    }

    setIsEvaluating(true);
    setEvaluationError(null);
    setShowPreview(true);

    try {
      const result = await openRouterService.evaluatePrompt(content, language);
      setEvaluation(result);
      setEvaluatedContent(content); // Store the content that was evaluated
      
      // Add to history
      if (openRouterConfig) {
        addEvaluation(content, result, language, openRouterConfig.model);
      }
      
      // Track evaluation
      trackPromptEvaluated(
        language,
        openRouterConfig?.model || 'unknown',
        result.analysis.overallScore,
        result.suggestions.length
      );
    } catch (error) {
      setEvaluationError(error instanceof Error ? error.message : (isPortuguese ? 'Erro desconhecido' : 'Unknown error'));
    } finally {
      setIsEvaluating(false);
    }
  }, [openRouterService, content, language, isPortuguese, openRouterConfig, hasRecentEvaluation, addEvaluation]);

  const handleQuickPreview = useCallback(async () => {
    if (!openRouterService || !content.trim()) {
      if (!openRouterService) {
        alert(t('alerts.configureApiKey'));
        setShowConfig(true);
      }
      return;
    }

    setIsQuickPreviewing(true);
    setShowQuickPreview(true);
    setQuickPreviewResult('');

    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openRouterConfig?.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Aldeia Viva'
        },
        body: JSON.stringify({
          model: openRouterConfig?.model || 'anthropic/claude-3.5-sonnet',
          messages: [{ role: 'user', content: content }],
          max_tokens: openRouterConfig?.maxTokens || 500,
          temperature: openRouterConfig?.temperature || 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      const result = data.choices?.[0]?.message?.content || 'Sem resposta obtida';
      setQuickPreviewResult(result);
      
    } catch (error) {
      setQuickPreviewResult(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsQuickPreviewing(false);
    }
  }, [openRouterService, content, openRouterConfig, t]);

  const handleApplySuggestion = useCallback((suggestion: PromptSuggestion) => {
    if (!suggestion.example) return;
    
    // Replace the current content with the suggestion example
    const newContent = suggestion.example;
    setContent(newContent);
    setShowPreview(false);
    
    // Track suggestion application
    trackSuggestionApplied(suggestion.type, suggestion.severity, language);
  }, [language]);

  const handleSelectSnippet = useCallback((snippet: any) => {
    setContent(snippet.content);
    setShowSnippets(false);
    
    // Focus textarea after inserting snippet
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(snippet.content.length, snippet.content.length);
      }
    }, 100);
  }, []);

  const handleSaveCurrentAsSnippet = useCallback((name: string, description?: string) => {
    if (!content.trim()) return;
    
    try {
      addSnippet(name, content, description);
      // Force reload and update UI state
      setTimeout(() => {
        reloadSnippets();
        setAutoOpenSaveDialog(false);
      }, 100);
      
      return true; // Indicate success
    } catch (error) {
      console.error('Error saving snippet:', error);
      return false; // Indicate failure
    }
  }, [content, addSnippet, reloadSnippets]);

  // Handle multimodal result application
  const handleApplyMultimodalResult = useCallback((result: string) => {
    setContent(result);
    // Focus textarea after applying result
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(result.length, result.length);
      }
    }, 100);
  }, []);

  return (
    <div className={`min-h-screen transition-all duration-300 ${isFocusMode ? 'bg-gradient-to-br from-ghost-50 to-ghost-100' : 'bg-ghost-50'} ${className}`}>
      {/* Focus mode overlay */}
      {isFocusMode && (
        <div className="fixed inset-0 bg-ghost-50/80 backdrop-blur-sm z-10 animate-fade-in" />
      )}
      
      {/* Main editor container */}
      <div className={`relative transition-all duration-300 ${
        isFocusMode 
          ? 'max-w-65ch mx-auto py-12 sm:py-24 px-4 sm:px-8 z-20' 
          : 'max-w-4xl mx-auto py-8 sm:py-16 px-4 sm:px-6'
      }`}>
        
        {/* Header with title and focus indicator */}
        <div className={`mb-6 sm:mb-8 transition-all duration-300 ${isFocusMode ? 'opacity-30' : 'opacity-100'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <header>
              <h1 className="text-ghost-600 text-xs sm:text-sm font-medium tracking-wide">
                {isPortuguese ? 'Editor de Prompt IA Gratuito - Aldeia Viva' : 'Free AI Prompt Editor - Aldeia Viva'}
              </h1>
            </header>
            <div className="flex items-center space-x-3 sm:space-x-4">
              {isFocusMode && (
                <div className="text-xs text-ghost-400 animate-float-up hidden sm:block">
                  {isPortuguese ? 'Modo Foco' : 'Focus Mode'}
                </div>
              )}
              
              {/* Language Selector */}
              <LanguageSelector />
              
              {/* Magic Wand */}
              <MagicWand
                content={content}
                onContentImproved={setContent}
                openRouterConfig={openRouterConfig || undefined}
              />

              {/* Multimodal Button */}
              <button
                onClick={() => setShowMultimodal(true)}
                disabled={!openRouterConfig?.apiKey}
                className="text-ghost-400 hover:text-indigo-600 transition-colors duration-200 disabled:opacity-50"
                aria-label={isPortuguese ? "Análise Multimodal" : "Multimodal Analysis"}
                title={isPortuguese ? "Análise com múltiplos modelos e arquivos" : "Analysis with multiple models and files"}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </button>
              
              {/* Snippets Button */}
              <button
                onClick={() => setShowSnippets(true)}
                className="text-ghost-400 hover:text-ghost-600 transition-colors duration-200"
                aria-label={isPortuguese ? "Biblioteca de Snippets" : "Snippets Library"}
                title={isPortuguese ? "Ver biblioteca de snippets (Ctrl+Shift+S)" : "View snippets library (Ctrl+Shift+S)"}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </button>
              
              {/* Quick Preview Button */}
              <button
                onClick={handleQuickPreview}
                disabled={isQuickPreviewing || !content.trim()}
                className="text-ghost-400 hover:text-blue-600 transition-colors duration-200 disabled:opacity-50"
                aria-label={isPortuguese ? "Pré-visualizar Resultado" : "Quick Preview"}
                title={isPortuguese ? "Pré-visualizar resultado do prompt" : "Preview prompt result"}
              >
                {isQuickPreviewing ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              
              {/* Evaluate Prompt Button */}
              <button
                onClick={() => handleEvaluatePrompt()}
                disabled={isEvaluating || !content.trim()}
                className="text-ghost-400 hover:text-ghost-600 transition-colors duration-200 disabled:opacity-50"
                aria-label="Avaliar Prompt"
                title="Avaliar prompt com IA"
              >
                {isEvaluating ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                )}
              </button>

              {/* OpenRouter Config Button */}
              <button
                onClick={() => setShowConfig(true)}
                className="text-ghost-400 hover:text-ghost-600 transition-colors duration-200"
                aria-label="Configurar OpenRouter"
                title="Configurar API OpenRouter"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>

              <button
                onClick={exportContent}
                className="text-ghost-400 hover:text-ghost-600 transition-colors duration-200"
                aria-label="Exportar"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Main textarea */}
        <main className="relative group">
          <label htmlFor="prompt-editor" className="sr-only">
            {isPortuguese ? 'Digite seu prompt de inteligência artificial' : 'Enter your artificial intelligence prompt'}
          </label>
          
          {/* Inline Autocomplete Suggestions */}
          <InlineAutocompleteSuggestion
            suggestion={autocompleteSuggestion?.suggestion || ''}
            isLoading={isAutocompleteLoading}
            visible={!!autocompleteSuggestion && !isFocusMode}
            textareaRef={textareaRef}
            cursorPosition={cursorPosition}
          />
          
          <textarea
            id="prompt-editor"
            ref={textareaRef}
            value={content}
            onChange={handleTextChange}
            onSelect={handleSelection}
            onFocus={() => textareaRef.current?.addEventListener('selectionchange', handleSelection)}
            onBlur={() => textareaRef.current?.removeEventListener('selectionchange', handleSelection)}
            placeholder={isPortuguese ? 'Comece a escrever seu prompt de IA...' : 'Start writing your AI prompt...'}
            aria-label={isPortuguese ? 'Editor de prompt para inteligência artificial' : 'Artificial intelligence prompt editor'}
            aria-describedby="token-counter save-status"
            // Disable native autocorrect and suggestions
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            className={`
              w-full min-h-[50vh] sm:min-h-[60vh] p-4 sm:p-6 
              bg-transparent border-0 outline-none resize-none
              text-ghost-800 placeholder-ghost-300
              font-inter text-sm sm:text-base leading-relaxed
              transition-all duration-300
              ${isFocusMode ? 'text-base sm:text-lg leading-loose' : ''}
              focus:outline-none focus:ring-0
            `}
            style={{
              fontSize: isFocusMode ? (window.innerWidth >= 640 ? '1.125rem' : '1rem') : (window.innerWidth >= 640 ? '1rem' : '0.875rem'),
              lineHeight: isFocusMode ? '1.75' : '1.6',
            }}
          />
          
          {/* Subtle border that appears on focus */}
          <div className="absolute inset-0 pointer-events-none border border-transparent group-focus-within:border-ghost-200 rounded-lg transition-colors duration-200" />
        </main>

        {/* Floating toolbar */}
        {selectionRange && !isFocusMode && (
          <FloatingToolbar
            onBold={() => {/* implement bold */}}
            onItalic={() => {/* implement italic */}}
            onCode={() => {/* implement code */}}
            position={selectionRange}
          />
        )}

        {/* Template suggestions */}
        {suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 bg-white/90 backdrop-blur-md border border-ghost-200 rounded-lg shadow-lg animate-float-up w-full sm:w-auto min-w-48">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="block w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-ghost-700 hover:bg-ghost-100 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg"
                onClick={() => {/* implement suggestion selection */}}
              >
                {suggestion.name}
              </button>
            ))}
          </div>
        )}

        {/* Bottom bar */}
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0 mt-6 sm:mt-8 transition-all duration-300 ${
          isFocusMode ? 'opacity-40' : 'opacity-100'
        }`}>
          <div className="flex items-center space-x-4 sm:space-x-6 text-xs text-ghost-400">
            <div id="token-counter">
              <TokenCounter tokens={estimateTokens(content)} />
            </div>
            <div id="save-status" className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                saveStatus === 'saving' ? 'bg-amber-400 animate-pulse' :
                saveStatus === 'saved' ? 'bg-green-400' : 'bg-ghost-300'
              }`} />
              <span className="capitalize">{saveStatus === 'saving' ? (isPortuguese ? 'salvando' : 'saving') : saveStatus === 'saved' ? (isPortuguese ? 'salvo' : 'saved') : saveStatus === 'error' ? (isPortuguese ? 'erro' : 'error') : (isPortuguese ? 'inativo' : 'idle')}</span>
            </div>
          </div>
          
          <div className="text-xs text-ghost-400 space-x-2 sm:space-x-4 hidden sm:block">
            <span>⌘F {isPortuguese ? 'Foco' : 'Focus'}</span>
            <span>⌘H {isPortuguese ? 'Histórico' : 'History'}</span>
            <span>⌘E {isPortuguese ? 'Exportar' : 'Export'}</span>
            <span>⌘R {isPortuguese ? 'Avaliar' : 'Review'}</span>
            <span>⌘B {isPortuguese ? 'Sugerir' : 'Suggest'}</span>
            <span>⌘⇧S {isPortuguese ? 'Snippets' : 'Snippets'}</span>
            <span>⌘⌥L {isPortuguese ? 'Salvar' : 'Save'}</span>
          </div>
        </div>
      </div>

      {/* Version History Modal */}
      {showHistory && (
        <VersionHistory
          onClose={() => setShowHistory(false)}
          onRestore={(version) => {
            setContent(version);
            setShowHistory(false);
          }}
        />
      )}

      {/* OpenRouter Configuration Modal */}
      {showConfig && (
        <OpenRouterConfig
          isOpen={showConfig}
          onClose={() => setShowConfig(false)}
          onSave={handleConfigSave}
          currentConfig={openRouterConfig || undefined}
        />
      )}

      {/* Prompt Preview Modal */}
      {showPreview && (
        <PromptPreview
          evaluation={evaluation}
          isLoading={isEvaluating}
          error={evaluationError}
          onClose={() => {
            setShowPreview(false);
            setEvaluation(null);
            setEvaluationError(null);
          }}
          onApplySuggestion={handleApplySuggestion}
          onReanalyze={() => handleEvaluatePrompt(true)}
          currentContent={content}
          evaluatedContent={evaluatedContent}
        />
      )}

      {/* SEO Content and FAQ - Hidden from UI but visible to search engines */}
      <SEOContent />
      
      {/* FAQ Section - Visible in UI */}
      {!isFocusMode && <FAQSection />}
      
      {/* Quick Preview Modal */}
      <QuickPreviewModal
        isOpen={showQuickPreview}
        isLoading={isQuickPreviewing}
        result={quickPreviewResult}
        onClose={() => {
          setShowQuickPreview(false);
          setQuickPreviewResult('');
        }}
      />
      
      {/* Snippets Modal */}
      <SnippetsModal
        isOpen={showSnippets}
        onClose={() => {
          setShowSnippets(false);
          setAutoOpenSaveDialog(false);
        }}
        onSelectSnippet={handleSelectSnippet}
        currentContent={content}
        onSaveCurrentAsSnippet={handleSaveCurrentAsSnippet}
        autoOpenSaveDialog={autoOpenSaveDialog}
        onSaveDialogOpened={() => setAutoOpenSaveDialog(false)}
      />
      
      {/* Cookie Consent */}
      <CookieConsent />
      
      {/* Multimodal Workflow */}
      <MultimodalWorkflow
        isOpen={showMultimodal}
        onClose={() => setShowMultimodal(false)}
        initialPrompt={content}
        onApplyResult={handleApplyMultimodalResult}
        baseConfig={openRouterConfig || undefined}
      />
    </div>
  );
};